import {
  CreateGenrePageApiModel,
  GenrePageApiModel,
  MarketingPageApiModel,
  PageApiModel,
  PageTypeApiModel,
  PageVersionStatusApiModel,
} from '@cxb/editor-stb-api';
import { CreatePageRequestApiModel, CreateRedeemPageRequestApiModel, RedeemPageApiModel } from '@cxb/editor-stb-api/model/models';
import { Country } from '../../domain/country';
import {
  SimpleContentPage,
  SimpleGenrePage,
  SimpleMarketingPage,
  SimplePageCommon,
  SimplePageType,
  SimpleRedeemPage,
} from '../../domain/simple-page';
import { UserSegment } from '../../domain/user-segment';
import { PageType } from '../../page-edit-screen/page-type';
import { ContentPageDetailDialogDataViewModel } from '../../shared/components/content-page-detail-dialog/content-page-detail-dialog-data-view.model';
import { RedeemPageDetailDialogDataViewModel } from '../../shared/components/redeem-page-detail-dialog/redeem-page-detail-dialog-data.view-model';

/* eslint-disable prefer-arrow/prefer-arrow-functions */

function mapApiPageToPage<T extends SimplePageType>(
  page: PageApiModel | RedeemPageApiModel | GenrePageApiModel,
  type: T,
  pageType: PageType
): SimplePageCommon<T> {
  const draftPageVersion = page.pageVersions.find((it) => it.status === PageVersionStatusApiModel.Draft);
  const livePageVersion = page.pageVersions.find((it) => it.status === PageVersionStatusApiModel.Published);
  return {
    type,
    id: page.id,
    draftPageId: draftPageVersion?.id,
    livePageId: livePageVersion?.id,
    slug: page.slug,
    name: page.internalName,
    lastPublishedAt: page.lastPublishedAt,
    pageType,
    pageStatus: draftPageVersion ? 'DRAFT' : 'PUBLISHED',
  };
}

export function mapContentApiPageToDomain(page: PageApiModel): SimpleContentPage {
  return mapApiPageToPage(page, 'Content', PageType.content);
}

export function mapGenreApiPageToDomain(page: GenrePageApiModel): SimpleGenrePage {
  return { ...mapApiPageToPage(page, 'Genre', PageType.genre), genre: page.genre };
}

export function mapMarketingApiPageToDomain(page: MarketingPageApiModel): SimpleMarketingPage {
  return mapApiPageToPage(page, 'Marketing', mapApiMarketingPageTypeToDomainPageType(page.type, page.internalName));
}

export function mapRedeemApiPageToDomain(countries: Country[], userSegments: UserSegment[], page: RedeemPageApiModel): SimpleRedeemPage {
  const activeVersion = page.pageVersions[0];
  const activeCountryIds = activeVersion.countries.map((it) => it.id);
  const activeUserSegmentIds = activeVersion.userSegments.map((it) => it.id);

  return {
    ...mapApiPageToPage(page, 'Redeem', PageType.redeem),
    countries: countries.filter((it) => activeCountryIds.includes(it.id)),
    userSegments: userSegments.filter((it) => activeUserSegmentIds.includes(it.id)),
    isGenericRedeem: page.slug === 'generic-redeem-page',
  };
}

export function mapCreateRedeemPageModelToApi(
  data: RedeemPageDetailDialogDataViewModel,
  platformIds: string[],
  tenantId: string
): CreateRedeemPageRequestApiModel {
  return {
    platformIds,
    countryGroupIds: [], // TODO
    countryIds: data.countryIds,
    userSegmentIds: data.userSegmentIds,
    internalName: data.internalName,
    nameLocalized: { en: data.tabTitleEnglish, ar: data.tabTitleEnglish },
    metaTags: data.metaTags,
    slug: data.pageUrl,
    tenantId,
  };
}

export function mapCreateContentPageModelToApi(
  data: ContentPageDetailDialogDataViewModel,
  tenantId: string
): CreatePageRequestApiModel | CreateGenrePageApiModel {
  // sending null value to BE when data is not set
  // when data is set, we have to send both en and ar fields, they cannot be nulls
  const seoTitle =
    data.SEOTitleEnglish || data.SEOTitleArabic
      ? {
          en: data.SEOTitleEnglish || '',
          ar: data.SEOTitleArabic || '',
        }
      : null;

  const seoDescription =
    data.SEODescriptionEnglish || data.SEODescriptionArabic
      ? { en: data.SEODescriptionEnglish || '', ar: data.SEODescriptionArabic || '' }
      : null;

  const commonPart = {
    internalName: data.internalName,
    nameLocalized: { en: data.tabTitleEnglish, ar: data.tabTitleArabic },
    seoTitle,
    seoDescription,
    metaTags: data.metaTags,
    tenantId,
  };
  return data.attachedGenre ? { ...commonPart, genre: data.attachedGenre } : commonPart;
}

const mapApiMarketingPageTypeToDomainPageType = (apiPageType: PageTypeApiModel, internalName: string): PageType => {
  switch (apiPageType) {
    case PageTypeApiModel.Content:
      return PageType.content;
    case PageTypeApiModel.Genre:
      return PageType.genre;
    case PageTypeApiModel.Landing:
      return PageType.landing;
    case PageTypeApiModel.Detail:
      return PageType.marketingDetails;
    case PageTypeApiModel.Collection:
      return PageType.collection;
    case PageTypeApiModel.AppLanding:
      return mapAppLandingPageTypeApiModelToPageType(apiPageType, internalName);
  }
};

const mapAppLandingPageTypeApiModelToPageType = (apiModel: PageTypeApiModel, internalName: string): PageType => {
  if (apiModel !== PageTypeApiModel.AppLanding) {
    return;
  }
  if (internalName.toLowerCase().includes('mobile')) {
    return PageType.appLandingMobile;
  } else if (internalName.toLowerCase().includes('tablet')) {
    return PageType.appLandingTablet;
  } else if (internalName.toLowerCase().includes('cotv')) {
    return PageType.appLandingCotv;
  } else if (internalName.toLowerCase().includes('apple')) {
    return PageType.appLandingAppleAndroidTv;
  }
};
